import type { BiomarkerResult, Protocol, SupplementRecommendation, HealthGoal } from '../../types';
import type { FormularyManifest, ManifestProduct, ManifestBundle, SafetyRule, Product } from '../../types/formulary';
import { routedGenerate } from '../ai/providerRouter';
import { getManifest, getActiveSafetyRules, getAllProducts } from '../firestore/formularyService';
import { preFilterProducts } from '../safety/safetyChecker';
// CLINICAL SAFETY: mockFallback is loaded via dynamic import ONLY when mockMode === true.
// This ensures Vite tree-shakes all fabricated clinical data out of the production bundle.
import { logAnalysisError } from '../firestore/analysisService';
import { getAgentBrand, type AgentBrandIdentity } from '../firestore/agentBrandService';
import {
  validateAndSafeGuardProtocol,
  emptySafetyProfile,
  type SafetyDataProfile,
} from '../safetyCheckerEngine';

// --- Cached manifest (avoids repeated Firestore reads) ---
let cachedManifest: FormularyManifest | null = null;
let cachedRules: SafetyRule[] | null = null;
let cachedProducts: Product[] | null = null;
let cacheTimestamp = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

async function getFormularyData() {
  const now = Date.now();
  if (cachedManifest && cachedRules && cachedProducts && now - cacheTimestamp < CACHE_TTL) {
    return { manifest: cachedManifest, rules: cachedRules, products: cachedProducts };
  }

  const [manifest, rules, products] = await Promise.all([
    getManifest(),
    getActiveSafetyRules(),
    getAllProducts(),
  ]);
  cachedManifest = manifest;
  cachedRules = rules;
  cachedProducts = products;
  cacheTimestamp = now;
  return { manifest, rules, products };
}

/**
 * Build a constrained system prompt that injects the formulary manifest.
 * Claude can ONLY recommend products from this list.
 */
function buildConstrainedPrompt(
  manifest: FormularyManifest | null,
  userMedications: string[],
  userAllergies: string[],
  _userConditions: string[],
  userSex?: 'female' | 'male',
  brand?: AgentBrandIdentity,
): string {
  // CLINICAL SAFETY: Refuse to generate protocol without formulary constraints.
  // An unconstrained prompt lets Claude recommend anything — bypassing safety rules.
  if (!manifest) {
    throw new Error('Formulary data is unavailable. Cannot generate a safe protocol without product constraints.');
  }

  // Filter products by gender
  const filteredProducts = manifest.products.filter(
    (p) => p.gender === 'any' || !userSex || p.gender === userSex,
  );

  // Build product list for prompt
  const tier1 = filteredProducts.filter((p) => p.tier === 1);
  const tier2 = filteredProducts.filter((p) => p.tier === 2);
  const tier3 = filteredProducts.filter((p) => p.tier === 3);

  const formatProduct = (p: ManifestProduct) =>
    `- ID:${p.id} | ${p.name} | ${p.dosage} | ${p.timing} | Targets: ${p.targetBiomarkers.join(', ')} | Evidence: ${p.evidenceLevel} | ${p.pricing.monthlyPrice || 'N/A'} | ${p.requiresRx ? 'Rx' : 'OTC'}${p.contraindications.length > 0 ? ` | CI: ${p.contraindications.join(', ')}` : ''}`;

  const formatBundle = (b: ManifestBundle) =>
    `- ID:${b.id} | ${b.name} (Tier ${b.tier}) | ${b.priceRange} | Products: ${b.productNames.join(', ')} | Target: ${b.target} | ${b.requiresProvider ? 'Requires provider' : 'Direct'}`;

  const b = brand ?? { kFrameworkOwner: "Stan Van Meter", clinicalPhilosophy: "evidence-based reasoning, intellectual honesty, and patient autonomy", protocolBuilderRole: "HemoLogix Protocol Builder", platformNameFormal: "HemoLogix" };
  return `## Layer 1 — ${b.kFrameworkOwner}'s Bio/Ethics/K-Framework
You are grounded in ${b.kFrameworkOwner}'s knowledge framework (K-framework): ${b.clinicalPhilosophy}. You never recommend products without clinical rationale, never overstate supplement efficacy, and always include appropriate disclaimers. You prioritize safety over sales.

## Layer 2 — ${b.platformNameFormal} Platform Context
You are the ${b.protocolBuilderRole} — an expert clinical protocol generator within the ${b.platformNameFormal} platform. You receive structured biomarker data from the Lab Analyst (Captain) and generate personalized supplement, medication, and longevity protocols. Your output drives the patient's actionable care plan.

## Layer 3 — Clinical Expertise: Longevity Medicine · HRT · Functional Medicine
You combine the perspectives of three clinical disciplines:
- **Longevity Medical Expert**: You design protocols informed by centenarian research, healthspan optimization, and anti-aging interventions (NAD+, senolytics, mitochondrial support).
- **HRT Medical Expert**: You recognize when hormonal optimization (testosterone, estradiol, thyroid, DHEA) is indicated and recommend appropriate Rx-tier interventions requiring provider consultation.
- **Functional Medicine Medical Expert**: You address root causes — gut health, methylation, detox pathways, adrenal function — not just isolated biomarker deficiencies.

CRITICAL CONSTRAINT: You may ONLY recommend products from the APPROVED FORMULARY below. Every recommendation MUST include the exact product ID. Do NOT invent products.

===== APPROVED FORMULARY — TIER 1 (Supplements, Direct Checkout) =====
${tier1.map(formatProduct).join('\n')}

===== APPROVED FORMULARY — TIER 2 (Rx, Requires $49 Provider Visit) =====
${tier2.map(formatProduct).join('\n')}

===== APPROVED FORMULARY — TIER 3 (Longevity, Premium) =====
${tier3.map(formatProduct).join('\n')}

===== APPROVED BUNDLES =====
${manifest.bundles.filter((b) => b.gender === 'any' || !userSex || b.gender === userSex).map(formatBundle).join('\n')}

RULES:
1. ONLY recommend products with IDs from the APPROVED FORMULARY above
2. For each recommendation, include the exact product ID (e.g., "supp-vitamin-d3-k2")
3. Prioritize by: (a) deviation from optimal biomarker range, (b) alignment with stated health goals, (c) evidence level
4. Group by timing: AM_ENERGY (morning with food), MIDDAY_ABSORPTION (between meals), PM_RECOVERY (evening/bedtime)
5. If ≥3 products match a bundle, include the bundle name and price
6. Primary recommendations should be Tier 1 supplements (6-10 items)
7. If biomarkers warrant it, suggest 1-2 Tier 2 Rx options clearly labeled as "requires provider consultation"
8. Respect medication interactions — user takes: ${userMedications.join(', ') || 'none reported'}
9. Respect allergies: ${userAllergies.join(', ') || 'none reported'}
10. Sex-aware: filter by patient sex, don't recommend male products for female patients and vice versa
11. Maximum 10 primary supplements + up to 3 Rx suggestions
12. Frame as optimization not treatment. Include DSHEA disclaimer.

Return ONLY valid JSON:
{
  "supplements": [
    {
      "productId": "supp-vitamin-d3-k2",
      "name": "Vitamin D3 + K2",
      "dosage": "5000 IU D3 + 100mcg K2 MK-7",
      "timing": "AM_ENERGY",
      "rationale": "Your Vitamin D at 28 ng/mL is below functional optimal (50-80)...",
      "targetBiomarker": "Vitamin D (25-OH)",
      "gender": "both",
      "contraindications": [],
      "interactionWarnings": [],
      "evidenceLevel": "strong",
      "requiresRx": false
    }
  ],
  "summary": "Protocol summary",
  "tierRecommendation": 1,
  "warnings": [],
  "bundleName": "Essential Foundation",
  "bundlePrice": "$149/mo",
  "bundleId": "bundle-essential-foundation",
  "safetyWarnings": []
}`;
}


export async function runProtocolBuilder(
  biomarkers: BiomarkerResult[],
  goals: HealthGoal[],
  medications: string[],
  allergies: string[],
  sex: 'female' | 'male' | undefined,
  mockMode: boolean,
  isPregnant?: boolean,
  safetyProfile?: SafetyDataProfile,
): Promise<Protocol> {
  // CLINICAL SAFETY: Mock mode is ONLY for demo/testing (VITE_ENABLE_MOCK=true).
  // When a real API call fails, we throw an error — we NEVER silently substitute
  // a fabricated protocol. A patient must never receive fake recommendations as if they were real.
  if (mockMode) {
    const { getMockProtocol } = await import('./mockFallback');
    await new Promise((r) => setTimeout(r, 1000));
    return getMockProtocol();
  }

  try {
    // 1. Fetch formulary data + brand identity (cached)
    const [{ manifest, rules, products }, brand] = await Promise.all([
      getFormularyData(),
      getAgentBrand(),
    ]);

    // 2. Build aggregated safety profile from all sources (modals + chat disclosures)
    // [c4] Uses latest safety data from all sources before protocol generation
    const profile: SafetyDataProfile = safetyProfile ?? {
      ...emptySafetyProfile(),
      sex,
      age: undefined,
      isPregnant,
      modalMedications: medications,
      modalAllergies: allergies,
    };

    // 3. Pre-filter: remove products blocked by safety rules (including pregnancy)
    const safeProducts = products.length > 0
      ? preFilterProducts(products, medications, [], allergies, sex, isPregnant, rules)
      : [];

    // 4. Build constrained prompt with brand identity
    const systemPrompt = buildConstrainedPrompt(manifest, medications, allergies, [], sex, brand);

    // UC-2: Include trajectory data when available for longitudinal context
    const biomarkerLines = biomarkers.map((b) => {
      let line = `- ${b.biomarker}: ${b.value} ${b.unit} (status: ${b.hemologixStatus}, category: ${b.category})`;
      if (b.trajectory) {
        line += ` [trajectory: ${b.trajectory.direction} — ${b.trajectory.summary}]`;
      }
      return line;
    });

    const userMessage = `Generate a personalized protocol based on this data:

BIOMARKERS:
${biomarkerLines.join('\n')}

HEALTH GOALS: ${goals.join(', ') || 'general optimization'}
CURRENT MEDICATIONS: ${medications.join(', ') || 'none'}
ALLERGIES: ${allergies.join(', ') || 'none'}
SEX: ${sex || 'not specified'}

When biomarkers show trajectory data, reference the trend in your rationale (e.g., "Your Vitamin D improved from 22 to 48 — continue supplementation to reach optimal range").

Return JSON only.`;

    // 5. Call via Provider Router (failover-aware)
    const routerResult = await routedGenerate(
      'protocol-builder',
      systemPrompt,
      userMessage,
    );
    if (routerResult.fallbackUsed) {
      console.warn(`[Protocol Builder] Fallback used: ${routerResult.provider}/${routerResult.model}`);
    }
    const response = routerResult.text;

    const protocol = parseProtocolResponse(response);

    // 6. [c1] Safety Checker Orchestration — runs BEFORE protocol is returned/displayed
    // [c2] If safety check fails, strips blocked products and logs exclusions
    // [c3] All exclusions audit-logged with SafetyRule.id and reasoning
    // [c4] Re-validates against latest safety data from all sources
    if (safeProducts.length > 0 && protocol.supplements.length > 0) {
      const orchestratorResult = await validateAndSafeGuardProtocol(
        protocol,
        profile,
        rules,
        products,
      );

      // Return the safety-validated protocol
      return orchestratorResult.protocol;
    }

    return protocol;
  } catch (error) {
    console.error('[Protocol Builder] Error:', error);
    logAnalysisError('protocol-builder', error instanceof Error ? error.message : String(error), {
      biomarkerCount: biomarkers.length,
      goalCount: goals.length,
    }).catch(() => {});
    throw new Error('Unable to generate your protocol right now. Please try again in a few minutes.');
  }
}

function parseProtocolResponse(response: string): Protocol {
  let cleaned = response.trim();
  if (cleaned.startsWith('```')) {
    cleaned = cleaned.replace(/^```(?:json)?\n?/, '').replace(/\n?```$/, '');
  }

  const parsed = JSON.parse(cleaned);

  return {
    id: `protocol-${Date.now()}`,
    createdAt: new Date().toISOString(),
    supplements: (parsed.supplements || []).map((s: Record<string, unknown>) => ({
      productId: s.productId ? String(s.productId) : undefined,
      name: String(s.name || ''),
      dosage: String(s.dosage || ''),
      timing: (['AM_ENERGY', 'MIDDAY_ABSORPTION', 'PM_RECOVERY'].includes(String(s.timing))
        ? String(s.timing)
        : 'AM_ENERGY') as SupplementRecommendation['timing'],
      rationale: String(s.rationale || ''),
      targetBiomarker: String(s.targetBiomarker || ''),
      gender: (['M', 'F', 'both'].includes(String(s.gender)) ? String(s.gender) : 'both') as 'M' | 'F' | 'both',
      contraindications: Array.isArray(s.contraindications) ? s.contraindications.map(String) : [],
      interactionWarnings: Array.isArray(s.interactionWarnings) ? s.interactionWarnings.map(String) : [],
      evidenceLevel: (['strong', 'moderate', 'emerging'].includes(String(s.evidenceLevel))
        ? String(s.evidenceLevel)
        : 'moderate') as SupplementRecommendation['evidenceLevel'],
      requiresRx: s.requiresRx === true,
    })),
    summary: String(parsed.summary || ''),
    tierRecommendation: (Number(parsed.tierRecommendation) || 1) as Protocol['tierRecommendation'],
    warnings: Array.isArray(parsed.warnings) ? parsed.warnings.map(String) : [],
    bundleName: parsed.bundleName ? String(parsed.bundleName) : undefined,
    bundlePrice: parsed.bundlePrice ? String(parsed.bundlePrice) : undefined,
    bundleId: parsed.bundleId ? String(parsed.bundleId) : undefined,
    safetyWarnings: Array.isArray(parsed.safetyWarnings) ? parsed.safetyWarnings.map(String) : undefined,
  };
}

